// ─── Web Audio API (único contexto compartilhado) ─────────────────────────────

let ctx: AudioContext | null = null;

// Retoma o contexto na primeira interação do usuário (toque/clique/tecla) para
// que sons em resposta a eventos de background (WS, polling) já encontrem o
// contexto rodando.
function attachUnlockListeners(): void {
    const unlock = () => {
        if (ctx && ctx.state === 'suspended') void ctx.resume();
    };
    (['click', 'touchstart', 'keydown'] as const).forEach((ev) =>
        document.addEventListener(ev, unlock, { passive: true }),
    );
}

if (typeof document !== 'undefined') {
    attachUnlockListeners();
}

function ac(): AudioContext {
    if (!ctx || ctx.state === 'closed') {
        ctx = new AudioContext();
        // Tenta retomar imediatamente (navegadores desktop costumam aceitar
        // se já houve qualquer interação na página).
        void ctx.resume();
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
}

// ─── Fallbacks sintéticos ─────────────────────────────────────────────────────

function beep(freq: number, dur: number, start: number, vol = 0.35): void {
    const c = ac();
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain);
    gain.connect(c.destination);
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(vol, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.start(start);
    osc.stop(start + dur + 0.05);
}

function beepNewOrder(): void {
    const t = ac().currentTime;
    beep(523, 0.14, t);
    beep(659, 0.14, t + 0.17);
    beep(784, 0.28, t + 0.34);
}

function beepOrderAssigned(): void {
    const t = ac().currentTime;
    beep(659, 0.14, t);
    beep(784, 0.28, t + 0.17);
}

function beepArrived(): void {
    beep(880, 0.45, ac().currentTime, 0.2);
}

function beepPixClaimed(): void {
    const t = ac().currentTime;
    beep(440, 0.12, t, 0.25);
    beep(554, 0.20, t + 0.15, 0.25);
}

function beepDeliveryProblem(): void {
    const t = ac().currentTime;
    beep(660, 0.15, t, 0.3);
    beep(495, 0.15, t + 0.18, 0.3);
    beep(392, 0.30, t + 0.36, 0.3);
}

function beepCounterSale(): void {
    const t = ac().currentTime;
    beep(880, 0.08, t, 0.3);
    beep(1047, 0.08, t + 0.10, 0.3);
    beep(1319, 0.18, t + 0.20, 0.3);
}

function beepNewChatMessage(): void {
    const t = ac().currentTime;
    beep(660, 0.10, t, 0.22);
    beep(880, 0.14, t + 0.11, 0.22);
}

// ─── MP3 via AudioContext (funciona sem gesto simultâneo do usuário) ──────────
// new Audio().play() é bloqueado por autoplay policy em eventos de background
// (WebSocket, push). AudioContext já desbloqueado pelo primeiro clique funciona.

const bufferCache = new Map<string, AudioBuffer>();

async function loadBuffer(path: string): Promise<AudioBuffer | null> {
    const cached = bufferCache.get(path);
    if (cached) return cached;
    try {
        const res = await window.fetch(path);
        if (!res.ok) return null;
        const raw = await res.arrayBuffer();
        const buf = await ac().decodeAudioData(raw);
        bufferCache.set(path, buf);
        return buf;
    } catch {
        return null;
    }
}

function playBuffer(buf: AudioBuffer, vol = 0.75): void {
    const c = ac();
    const src = c.createBufferSource();
    const gain = c.createGain();
    src.buffer = buf;
    gain.gain.value = vol;
    src.connect(gain);
    gain.connect(c.destination);
    src.start(0);
}

function playMp3(path: string, fallback: () => void): void {
    loadBuffer(path).then((buf) => {
        if (buf) {
            try { playBuffer(buf); } catch { try { fallback(); } catch {} }
        } else {
            try { fallback(); } catch {}
        }
    }).catch(() => {
        try { fallback(); } catch {}
    });
}

// ─── API pública ──────────────────────────────────────────────────────────────

/** PDV — novo pedido recebido */
export function playNewOrderSound(): void {
    playMp3('/sounds/new-order.mp3', beepNewOrder);
}

/** Entregador — pedido atribuído */
export function playOrderAssignedSound(): void {
    playMp3('/sounds/order-assigned.mp3', beepOrderAssigned);
}

/** Cliente — entregador chegou */
export function playDelivererArrivedSound(): void {
    playMp3('/sounds/arrived.mp3', beepArrived);
}

/** PDV — cliente informou pagamento PIX (manual) */
export function playPixClaimedSound(): void {
    playMp3('/sounds/chegou-um-pix.mp3', beepPixClaimed);
}

/** Chat — nova mensagem recebida (operador: do cliente; cliente: do operador).
 *  Silencia quando a janela está em foco — se já está olhando a aba, vê a
 *  mensagem chegar e o som vira ruído. Só alerta quando está em outra aba/app. */
export function playNewChatMessageSound(): void {
    if (typeof document !== 'undefined' && document.hasFocus()) return;
    playMp3('/sounds/nova-mensagem-chat.mp3', beepNewChatMessage);
}

/** PDV — entregador reportou problema */
export function playDeliveryProblemSound(): void {
    playMp3('/sounds/delivery-problem.mp3', beepDeliveryProblem);
}

/** PDV — venda no balcão finalizada */
export function playCounterSaleSound(): void {
    playMp3('/sounds/counter-sale.mp3', beepCounterSale);
}
